import React, { useState } from "react";
import Image from "next/image";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";
import { ModalBase, message } from "@aspen/ui";
import { i18nUtil, BANK_TRANSFER_DETAILS_SECTIONS } from "@aspen/libs";
import bankCopyIcon from "@aspen/assets/images/bank_copy.webp";
import type { ModalProps } from "antd";
import { CommonButton } from "@adgm/cross-ui";

interface IModalBankTransferDetailsProps extends ModalProps {
  visible: boolean;
  onClose: () => void;
  note?: string;
}

export const ModalBankTransferDetails: React.FC<IModalBankTransferDetailsProps> = ({
  visible,
  onClose,
  note,
  ...rest
}) => {
  const [copiedIndex, setCopiedIndex] = useState<string>("");
  const intl = i18nUtil.t();
  const title = intl["deposit.otc.bank.transfer.details.modal.title"];
  const closeButtonText = intl["deposit.otc.bank.transfer.details.modal.close"];

  const handleCopy = (value: string, index: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        setCopiedIndex(index);
        message.success(intl["copied"]);
        setTimeout(() => {
          setCopiedIndex("");
        }, 3000);
      },
      () => {
        message.error(intl["copy.failed"]);
      },
    );
  };

  return (
    <ModalBase
      title={title}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={577}
      closable={false}
      className="bank-transfer-modal"
      bodyStyle={{
        padding: "0",
        height: "540px",
        display: "flex",
        flexDirection: "column",
      }}
      {...rest}>
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-10 p-0 bg-none border-none cursor-pointer text-white/60 hover:text-white transition-colors"
        type="button"
        aria-label="Close">
        <CloseOutlined style={{ fontSize: "16px" }} />
      </button>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto w-full">
        {BANK_TRANSFER_DETAILS_SECTIONS.map((section, sectionIndex) => (
          <div
            key={section.title}
            className="border-t border-[#3e414f]"
            style={{ borderTopStyle: "solid" }}>
            {/* Section Title */}
            <div className="flex items-center px-6 py-2 bg-white/10">
              <h3 className="text-sm font-common-medium text-white">{intl[section.title]}</h3>
            </div>

            {/* Section Content - Horizontal Layout */}
            <div className="w-full">
              {section.items.map((item, itemIndex) => {
                const itemKey = `${section.title}-${itemIndex}`;
                const isCopied = copiedIndex === itemKey;
                const isLastSection = sectionIndex === BANK_TRANSFER_DETAILS_SECTIONS.length - 1;
                const isLastItem = itemIndex === section.items.length - 1;
                const shouldRemoveBorder = !isLastSection && isLastItem;

                return (
                  <div
                    key={`item-${itemKey}`}
                    className={`flex items-center gap-4 border-b border-[#3e414f] ${shouldRemoveBorder ? "border-b-0" : ""}`}
                    style={{ borderBottomStyle: "solid" }}>
                    <div
                      className="w-[242px] flex-shrink-0 text-sm font-common-medium text-white border-r border-[#3e414f] pl-6 flex items-center self-stretch"
                      style={{ borderRightStyle: "solid" }}>
                      {intl[item.label]}
                    </div>
                    <div className="flex-1 break-words text-sm font-common-medium text-white whitespace-pre-wrap leading-relaxed py-2 pr-4">
                      {item.value}
                    </div>
                    <button
                      className="flex-shrink-0 bg-none border-none cursor-pointer p-0 inline-flex items-center justify-center text-white/70 hover:text-white transition-colors py-2 pr-6"
                      onClick={() => handleCopy(item.value, itemKey)}
                      title={isCopied ? intl["copied"] : intl["copy"]}
                      type="button">
                      {isCopied ? (
                        <CheckOutlined style={{ color: "#f17e00", fontSize: "12px" }} />
                      ) : (
                        <Image src={bankCopyIcon} alt="copy" width={12} height={12} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Note Section */}
      {note && (
        <div className="px-6 py-2 text-xs text-white/60 leading-relaxed font-common-medium">
          {note}
        </div>
      )}

      {/* Footer - Close Button */}
      <div className="px-6 py-6 flex-shrink-0">
        <CommonButton onPress={onClose} text={closeButtonText} />
      </div>
    </ModalBase>
  );
};
